import { useCallback, useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "../../lib/api";

export function useResource(path, deps = []) {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setState("loading");
    try {
      const res = await api.get(path);
      setData(res.data);
      setState("ready");
    } catch (e) {
      setError(apiError(e, "We couldn't load this data."));
      setState("error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, state, error, reload: load, setData };
}

export const PageTitle = ({ title, sub, action, testId }) => (
  <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
    <div>
      <h1 data-testid={testId} className="text-2xl font-semibold text-elv-text sm:text-3xl">
        {title}
      </h1>
      {sub ? <p className="mt-2 max-w-2xl text-sm text-elv-dim">{sub}</p> : null}
    </div>
    {action}
  </div>
);

export const Panel = ({ title, action, children, className = "", testId, footer }) => (
  <section data-testid={testId} className={`glass rounded-lg ${className}`}>
    {title ? (
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-elv-line px-5 py-3.5">
        <h2 className="text-[11px] uppercase tracking-[0.2em] text-elv-dim">{title}</h2>
        {action}
      </header>
    ) : null}
    <div className="p-5">{children}</div>
    {footer ? <div className="border-t border-elv-line px-5 py-3 text-xs text-elv-dim">{footer}</div> : null}
  </section>
);

export const Loading = ({ rows = 5, testId = "loading-state" }) => (
  <div data-testid={testId} className="grid gap-2">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-12 animate-pulse rounded-md border border-elv-line bg-white/[0.02]" />
    ))}
  </div>
);

export const ErrorState = ({ message, onRetry, testId = "error-state" }) => (
  <div data-testid={testId} className="rounded-lg border border-red-400/20 bg-red-400/[0.04] p-8 text-center">
    <Icons.AlertOctagon className="mx-auto h-6 w-6 text-red-300" />
    <p className="mt-4 text-sm text-elv-text">{message || "Something went wrong."}</p>
    {onRetry ? (
      <button
        type="button"
        onClick={onRetry}
        data-testid={`${testId}-retry`}
        className="mt-5 rounded-md bg-elv-green px-4 py-2 text-xs font-semibold text-[#04120A]"
      >
        Retry
      </button>
    ) : null}
  </div>
);

export const EmptyState = ({ icon = "Inbox", title, sub, action, testId = "empty-state" }) => {
  const Icon = Icons[icon] || Icons.Inbox;
  return (
    <div data-testid={testId} className="rounded-lg border border-elv-line bg-white/[0.015] p-10 text-center">
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg border border-elv-green/20 bg-elv-green/[0.06]">
        <Icon className="h-5 w-5 text-elv-green" />
      </span>
      <p className="mt-5 text-base font-medium text-elv-text">{title}</p>
      {sub ? <p className="mx-auto mt-2 max-w-sm text-sm text-elv-dim">{sub}</p> : null}
      {action ? <div className="mt-6 flex justify-center">{action}</div> : null}
    </div>
  );
};

export const Metric = ({ label, value, suffix, hint, tone = "green", testId }) => (
  <div data-testid={testId} className="glass glass-hover rounded-lg p-4">
    <p className="text-[10px] uppercase tracking-[0.18em] text-elv-dim">{label}</p>
    <p className="mt-3 font-display text-2xl text-elv-text sm:text-3xl">
      <AnimatedNumber value={value} />
      {suffix ? <span className="text-sm text-elv-dim">{suffix}</span> : null}
    </p>
    {hint ? <p className="mt-2 text-[11px] text-elv-dim">{hint}</p> : null}
    <div
      className={`mt-3 h-0.5 w-full bg-gradient-to-r ${
        tone === "cyan" ? "from-elv-cyan/60" : "from-elv-green/60"
      } to-transparent`}
    />
  </div>
);

export function AnimatedNumber({ value }) {
  const target = Number(value) || 0;
  const [shown, setShown] = useState(0);
  useEffect(() => {
    let frame;
    const start = performance.now();
    const duration = 700;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      setShown(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target]);
  const decimals = Number.isInteger(target) ? 0 : 1;
  return <>{shown.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}</>;
}

export const Pill = ({ children, tone = "dim", testId }) => {
  const tones = {
    dim: "border-white/10 bg-white/[0.03] text-elv-dim",
    green: "border-elv-green/25 bg-elv-green/[0.08] text-elv-green",
    cyan: "border-elv-cyan/25 bg-elv-cyan/[0.07] text-elv-cyan",
    amber: "border-amber-300/25 bg-amber-300/[0.07] text-amber-200",
    red: "border-red-400/25 bg-red-400/[0.07] text-red-300",
    purple: "border-[#6B4C9A]/35 bg-[#6B4C9A]/[0.12] text-[#c3a6e8]",
  };
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm border px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] ${tones[tone]}`}
    >
      {children}
    </span>
  );
};

export const STATUS_TONE = {
  new: "cyan", contacted: "dim", qualified: "green", booked: "green", won: "green",
  lost: "red", open: "cyan", done: "green", paid: "green", pending: "amber",
  overdue: "red", refunded: "purple", scheduled: "cyan", completed: "green",
  cancelled: "red", no_show: "amber", missed: "amber", hot: "red", warm: "amber", cold: "cyan",
};

export const DemoBadge = ({ label = "Demo Data", testId = "demo-data-badge" }) => (
  <span
    data-testid={testId}
    className="inline-flex items-center gap-1.5 rounded-sm border border-elv-cyan/25 bg-elv-cyan/[0.06] px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] text-elv-cyan"
  >
    <Icons.FlaskConical className="h-3 w-3" />
    {label}
  </span>
);

export const fmtDate = (v) =>
  v ? new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export const fmtDateTime = (v) =>
  v
    ? new Date(v).toLocaleString("en-GB", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
      })
    : "—";

export const fmtMoney = (v) => `$${Number(v || 0).toLocaleString()}`;

export const fmtDuration = (s) => {
  const n = Number(s || 0);
  if (!n) return "—";
  return `${Math.floor(n / 60)}m ${n % 60}s`;
};
